/**
 * Entitlement Service
 * Handles entitlement checks and management
 */

import type {
  EntitlementCheckResult,
  Entitlement,
  AccessLevel,
  EntitlementType
} from '../types/entitlement';

const STORAGE_KEY = 'user_entitlements';

// Default trial configuration
const TRIAL_CONFIG = {
  durationDays: 14,
  accessLevel: 'pro' as AccessLevel,
  features: ['pdf_export', 'nfpa_references', 'unlimited_projects', 'priority_support']
};

// Default demo configuration
const DEMO_CONFIG = {
  durationHours: 24,
  accessLevel: 'pro' as AccessLevel,
  features: ['pdf_export', 'nfpa_references']
};

class EntitlementService {
  /**
   * Check entitlements for a user
   */
  async checkEntitlements(userId: string): Promise<EntitlementCheckResult> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Check localStorage for demo purposes
    const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
    
    if (stored) {
      const entitlements: Entitlement[] = JSON.parse(stored);
      return this.evaluateEntitlements(entitlements);
    }
    
    // Default to free tier
    return {
      hasAccess: true,
      accessLevel: 'free',
      activeEntitlements: [],
      isGracePeriod: false,
      isTrial: false,
      isTeamLicense: false
    };
  }
  
  /**
   * Evaluate a list of entitlements and determine access
   */
  private evaluateEntitlements(entitlements: Entitlement[]): EntitlementCheckResult {
    const now = new Date();
    
    // Filter active entitlements
    const activeEntitlements = entitlements.filter(e => {
      if (e.status !== 'active') return false;
      if (e.endDate && new Date(e.endDate) < now) return false;
      return true;
    });
    
    if (activeEntitlements.length === 0) {
      return {
        hasAccess: true,
        accessLevel: 'free',
        activeEntitlements: [],
        isGracePeriod: false,
        isTrial: false,
        isTeamLicense: false
      };
    }
    
    // Find the highest access level entitlement
    const levelPriority: Record<AccessLevel, number> = {
      free: 0,
      basic: 1,
      pro: 2,
      business: 3,
      enterprise: 4,
      unlimited: 5
    };
    
    const primaryEntitlement = activeEntitlements.reduce((best, current) => {
      return levelPriority[current.accessLevel] > levelPriority[best.accessLevel] 
        ? current 
        : best;
    });
    
    // Calculate days remaining
    let daysRemaining: number | undefined;
    if (primaryEntitlement.endDate) {
      const endDate = new Date(primaryEntitlement.endDate);
      daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }
    
    return {
      hasAccess: true,
      accessLevel: primaryEntitlement.accessLevel,
      activeEntitlements,
      primaryEntitlement,
      isGracePeriod: false,
      isTrial: primaryEntitlement.type === 'trial',
      isTeamLicense: primaryEntitlement.type === 'team_license',
      organizationId: primaryEntitlement.organizationId,
      daysRemaining
    };
  }
  
  /**
   * Start a trial for a user
   */
  async startTrial(userId: string): Promise<Entitlement> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const now = new Date();
    const endDate = new Date(now.getTime() + TRIAL_CONFIG.durationDays * 24 * 60 * 60 * 1000);
    
    const trialEntitlement: Entitlement = {
      id: `trial_${userId}_${Date.now()}`,
      userId,
      type: 'trial',
      status: 'active',
      accessLevel: TRIAL_CONFIG.accessLevel,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      metadata: {
        features: TRIAL_CONFIG.features
      }
    };
    
    this.saveEntitlement(userId, trialEntitlement);
    return trialEntitlement;
  }
  
  /**
   * Start a demo for a user
   */
  async startDemo(userId: string): Promise<Entitlement> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const now = new Date();
    const endDate = new Date(now.getTime() + DEMO_CONFIG.durationHours * 60 * 60 * 1000);
    
    const demoEntitlement: Entitlement = {
      id: `demo_${userId}_${Date.now()}`,
      userId,
      type: 'demo',
      status: 'active',
      accessLevel: DEMO_CONFIG.accessLevel,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      metadata: {
        features: DEMO_CONFIG.features
      }
    };
    
    this.saveEntitlement(userId, demoEntitlement);
    return demoEntitlement;
  }
  
  /**
   * Grant curriculum access to a user
   */
  async grantCurriculumAccess(
    userId: string,
    courseId: string,
    courseName: string,
    durationDays: number
  ): Promise<Entitlement> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const now = new Date();
    const endDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
    
    const curriculumEntitlement: Entitlement = {
      id: `curriculum_${userId}_${courseId}_${Date.now()}`,
      userId,
      type: 'curriculum',
      status: 'active',
      accessLevel: 'pro',
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      courseId,
      courseName,
      metadata: {
        features: ['pdf_export', 'nfpa_references', 'unlimited_calculations']
      }
    };
    
    this.saveEntitlement(userId, curriculumEntitlement);
    return curriculumEntitlement;
  }
  
  /**
   * Save an entitlement to storage
   */
  private saveEntitlement(userId: string, entitlement: Entitlement): void {
    const key = `${STORAGE_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    const entitlements: Entitlement[] = stored ? JSON.parse(stored) : [];
    
    // Add new entitlement
    entitlements.push(entitlement);
    
    localStorage.setItem(key, JSON.stringify(entitlements));
  }
  
  /**
   * Get all entitlements for a user
   */
  async getEntitlements(userId: string): Promise<Entitlement[]> {
    const key = `${STORAGE_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }
  
  /**
   * Cancel an entitlement
   */
  async cancelEntitlement(userId: string, entitlementId: string): Promise<void> {
    const key = `${STORAGE_KEY}_${userId}`;
    const stored = localStorage.getItem(key);
    
    if (stored) {
      const entitlements: Entitlement[] = JSON.parse(stored);
      const updated = entitlements.map(e => 
        e.id === entitlementId ? { ...e, status: 'canceled' as const } : e
      );
      localStorage.setItem(key, JSON.stringify(updated));
    }
  }
}

export const entitlementService = new EntitlementService();
